/* eslint-disable import/no-cycle */
// eslint-disable-next-line import/no-extraneous-dependencies
import { createBrowserHistory, History } from 'history'
import { action, observable, makeObservable } from 'mobx'

import { getFfgUrlParams } from 'utils/url'
import { EntryPoint } from 'startup/command/loadEntryPoint'
import initializePage, {
  type PageStore
} from 'stores/initialization/pageInitialization'
import initializeFieldErrors, {
  type FieldErrorStore
} from 'stores/initialization/fieldErrorInitialization'
import initializeTraversalSession, {
  type TraversalSessionStore
} from 'stores/initialization/traversalSessionInitialization'
import initializeTraversal, {
  type TraversalStore
} from 'stores/initialization/traversalInitialization'
import initializeTheme, {
  type ThemeStore
} from 'stores/initialization/themeInitialization'
import initializeNavigation, {
  type Navigation
} from 'stores/initialization/navigationInitialization'
import initializeClientDetails, {
  type ClientDetailsStore
} from 'stores/initialization/clientDetailsInitialization'
import initializeFcid, {
  type FcidStore
} from 'stores/initialization/fcidInitialization'
import initializeCaptcha, {
  type CaptchaStore
} from 'stores/initialization/captchaInitialization'

import { LoadingMode } from '../components/LoadingProvider'

export function isInIframeContext() {
  if (typeof window === 'undefined' || !window) {
    return false
  }

  return window.top !== window
}

class RootStore {
  public traversal: TraversalStore
  public traversalSession: TraversalSessionStore
  public page: PageStore
  public fieldErrors: FieldErrorStore
  public theme: ThemeStore
  public history: History
  public navigation: Navigation
  public fcid: FcidStore
  public captcha: CaptchaStore
  public clientDetailsStore: ClientDetailsStore
  public urlParams = getFfgUrlParams(window.location.search)
  public isInIframe = isInIframeContext()
  public isFooterVisible = true
  public loadingMode = LoadingMode.Initial
  public initialEntryPoint: EntryPoint = EntryPoint.FURTHEST
  public experimentData: Record<string, string> = {}
  public resumeDetailsFetched = false
  public isDacShowOnCtaClick = false
  public isSkipDacCTAVisible = false
  public isDacProcessingIndicatorVisible = false

  constructor() {
    makeObservable(this, {
      urlParams: observable,
      isInIframe: observable,
      isFooterVisible: observable,
      loadingMode: observable,
      experimentData: observable,
      resumeDetailsFetched: observable,
      isDacShowOnCtaClick: observable,
      isSkipDacCTAVisible: observable,
      isDacProcessingIndicatorVisible: observable,
      setIsInIframe: action,
      setLoadingMode: action,
      setIsFooterVisible: action,
      setInitialEntryPoint: action,
      setResumeDetailsFetched: action,
      setIsDacShowOnCtaClick: action,
      setIsSkipDacCTAVisible: action,
      setIsDacProcessingIndicatorVisible: action
    })

    this.history = createBrowserHistory()
    this.page = initializePage(this)
    this.fieldErrors = initializeFieldErrors(this)
    this.traversalSession = initializeTraversalSession()
    this.traversal = initializeTraversal(this)
    this.theme = initializeTheme()
    this.navigation = initializeNavigation(this)
    this.clientDetailsStore = initializeClientDetails()
    this.fcid = initializeFcid(this.urlParams)
    this.captcha = initializeCaptcha()
  }

  setIsInIframe = (isInIframe: boolean) => {
    this.isInIframe = isInIframe
  }

  setLoadingMode = (loadingMode: LoadingMode) => {
    this.loadingMode = loadingMode
  }

  setIsFooterVisible = (isVisible: boolean) => {
    this.isFooterVisible = isVisible
  }

  setInitialEntryPoint(entryPoint: EntryPoint) {
    this.initialEntryPoint = entryPoint
  }

  setExperimentData = (experimentData: Record<string, string>) => {
    this.experimentData = experimentData
  }

  setResumeDetailsFetched = (resumeDetailsFetched: boolean) => {
    this.resumeDetailsFetched = resumeDetailsFetched
  }

  setIsDacShowOnCtaClick = (isDacShowOnCtaClick: boolean) => {
    this.isDacShowOnCtaClick = isDacShowOnCtaClick
  }

  setIsSkipDacCTAVisible = (isSkipDacCTAVisible: boolean) => {
    this.isSkipDacCTAVisible = isSkipDacCTAVisible
  }

  setIsDacProcessingIndicatorVisible = (
    isDacProcessingIndicatorVisible: boolean
  ) => {
    this.isDacProcessingIndicatorVisible = isDacProcessingIndicatorVisible
  }
}

export default RootStore
